<script setup lang="ts">
import { watch, nextTick, ref, computed } from 'vue';
import emblaCarouselVue from 'embla-carousel-vue';
import { storeToRefs } from 'pinia';
import { useDisplay } from 'vuetify';
import useLocale from '../../composables/useLocale';
import useNavigationStore from '../../stores/navigation';
import useRenditionStore from '../../stores/rendition';
import useNavigationTriggerStore from '../../stores/navigation-trigger';
import type { GoToItem } from '../../types';
import type { LoiItem } from '../../types/navigation';

type Emits = {
  (e: 'goToContent', item: GoToItem): void;
};
const emit = defineEmits<Emits>();

const { t } = useLocale();
const { setNavigationTrigger } = useNavigationTriggerStore();
const { platform, smAndUp } = useDisplay();
const isTouch = computed(() => platform.value.touch);
const isTouchLarge = computed(() => isTouch.value && smAndUp.value);

const { loi, currentSpineIndex, pages } = storeToRefs(useNavigationStore());
const { isSpread, isRTL } = storeToRefs(useRenditionStore());

const emblaDir = computed(() => (isRTL.value ? 'rtl' : 'ltr'));

const [emblaRef, emblaApi] = emblaCarouselVue({
  align: 'center',
  containScroll: 'trimSnaps',
  dragFree: true,
});

// Only src-in the images that are visible + a small look-ahead buffer.
// All slide DOM nodes always exist (embla needs them for measurement),
// but unloaded slides render a placeholder until they scroll into view.
const LOAD_BUFFER = 3;
const loadedIndices = ref(new Set<number>());

const loadSlidesInView = () => {
  const api = emblaApi.value;
  if (!api) return;
  api.slidesInView().forEach((i) => {
    for (let j = Math.max(0, i - LOAD_BUFFER); j <= Math.min(loi.value.length - 1, i + LOAD_BUFFER); j++) {
      loadedIndices.value.add(j);
    }
  });
};

// Wire up lazy loading once embla mounts
watch(emblaApi, (api) => {
  if (!api) return;
  api.on('slidesInView', loadSlidesInView);
  loadSlidesInView();
});

// When loi data arrives after async init, reInit embla so it measures the
// real slide layout, then scroll to the active slide only after embla's own
// reInit event fires (embla measures in a rAF, so nextTick alone is too early on mobile)
const activeLoiIdx = () => loi.value.findIndex((item) => item.spineIndex === currentSpineIndex.value);

watch(loi, async () => {
  loadedIndices.value = new Set();
  await nextTick();
  const api = emblaApi.value;
  if (!api) return;

  const afterReInit = () => {
    api.off('reInit', afterReInit);
    const idx = activeLoiIdx();
    if (idx !== -1) api.scrollTo(idx, false);
    loadSlidesInView();
  };

  api.on('reInit', afterReInit);
  api.reInit();
});

const spreadPartnerIndex = computed(() => (isRTL.value ? currentSpineIndex.value - 1 : currentSpineIndex.value + 1));

const isActive = (item: LoiItem): boolean =>
  item.spineIndex === currentSpineIndex.value || (isSpread.value && item.spineIndex === spreadPartnerIndex.value);

watch(currentSpineIndex, async () => {
  await nextTick();
  const idx = activeLoiIdx();
  if (idx !== -1) emblaApi.value?.scrollTo(idx);
  loadSlidesInView();
});

// Build href → page number map using the same substring matching as initializeLoi
const loiPageNumbers = computed(() => {
  const map = new Map<string, number>();
  for (const loiItem of loi.value) {
    const match = pages.value.find(
      (p) => p.href === loiItem.href || p.href.endsWith('/' + loiItem.href) || loiItem.href.endsWith(p.href),
    );
    if (match && !isNaN(match.page)) map.set(loiItem.href, match.page);
  }
  return map;
});

const pageNumForItem = (item: LoiItem): number | undefined => loiPageNumbers.value.get(item.href);

// Arrow key navigation
const onKeyDown = (event: KeyboardEvent) => {
  if (event.key === 'ArrowLeft') {
    emblaApi.value?.scrollPrev();
    event.preventDefault();
    event.stopPropagation();
  } else if (event.key === 'ArrowRight') {
    emblaApi.value?.scrollNext();
    event.preventDefault();
    event.stopPropagation();
  }
};

// Hover preview (desktop / pointer devices only)
const PREVIEW_WIDTH_SINGLE = 168;
const PREVIEW_WIDTH_SPREAD = 280;

const getSpreadPartner = (item: LoiItem, idx: number): LoiItem | null => {
  if (item.isSpreadPairLeft) {
    const next = loi.value[idx + 1];
    return next?.isSpreadPairRight ? next : null;
  }
  if (item.isSpreadPairRight) {
    const prev = loi.value[idx - 1];
    return prev?.isSpreadPairLeft ? prev : null;
  }
  return null;
};

const hoveredItem = ref<LoiItem | null>(null);
const hoveredPartner = ref<LoiItem | null>(null);
const previewStyle = ref<{ bottom: string; left: string } | null>(null);
let hoverTimer: ReturnType<typeof setTimeout> | null = null;

// Always left page first, right page second in the preview
const previewLeftItem = computed(() =>
  hoveredItem.value?.isSpreadPairLeft ? hoveredItem.value : (hoveredPartner.value ?? hoveredItem.value),
);

const previewRightItem = computed(() => {
  if (!hoveredPartner.value) return null;
  return hoveredItem.value?.isSpreadPairRight ? hoveredItem.value : hoveredPartner.value;
});

const previewHasPageNum = computed(
  () =>
    pageNumForItem(previewLeftItem.value!) !== undefined ||
    (previewRightItem.value !== null && pageNumForItem(previewRightItem.value) !== undefined),
);

const showPreviewFor = (el: HTMLElement, item: LoiItem, idx: number) => {
  const partner = getSpreadPartner(item, idx);
  const previewWidth = partner ? PREVIEW_WIDTH_SPREAD : PREVIEW_WIDTH_SINGLE;
  const rect = el.getBoundingClientRect();
  const left = Math.min(
    Math.max(rect.left + rect.width / 2 - previewWidth / 2, 8),
    window.innerWidth - previewWidth - 8,
  );
  previewStyle.value = {
    bottom: `${window.innerHeight - rect.top + 8}px`,
    left: `${left}px`,
  };
  hoveredItem.value = item;
  hoveredPartner.value = partner;
};

const onSlideMouseEnter = (event: MouseEvent, item: LoiItem, idx: number) => {
  if (isTouch.value) return;
  if (hoverTimer) {
    clearTimeout(hoverTimer);
    hoverTimer = null;
  }
  showPreviewFor(event.currentTarget as HTMLElement, item, idx);
};

const onSlideFocus = (event: FocusEvent, item: LoiItem, idx: number) => {
  if (isTouch.value) return;
  showPreviewFor(event.currentTarget as HTMLElement, item, idx);
};

const clearHoverPreview = () => {
  hoverTimer = setTimeout(() => {
    hoveredItem.value = null;
    previewStyle.value = null;
    hoverTimer = null;
  }, 80);
};

const keepHoverPreview = () => {
  if (hoverTimer) {
    clearTimeout(hoverTimer);
    hoverTimer = null;
  }
};

const onContainerTouchMove = (event: TouchEvent) => {
  const touch = event.touches[0];

  if (!isTouchLarge.value || !touch || !emblaRef.value) return;

  const slideEl = document.elementFromPoint(touch.clientX, touch.clientY)?.closest<HTMLElement>('.pp-slide');

  if (!slideEl) return;

  const slides = Array.from(emblaRef.value.querySelectorAll<HTMLElement>('.pp-slide'));
  const idx = slides.indexOf(slideEl);
  const item = loi.value[idx];

  if (idx !== -1 && item) {
    keepHoverPreview(); // Reuse your existing helper!
    showPreviewFor(slideEl, item, idx);
  }
};

const onContainerTouchEnd = () => {
  if (!isTouchLarge.value) return;
  clearHoverPreview();
};

const goToContent = (item: LoiItem) => {
  setNavigationTrigger('THUMBNAIL');
  emit('goToContent', {
    target: item.rawHref,
    isGoingForwardsInBook: isRTL.value
      ? item.spineIndex < currentSpineIndex.value
      : item.spineIndex > currentSpineIndex.value,
  });
};
</script>

<template>
  <div class="pp-embla-wrapper w-100" data-testid="bp-progress-previews">
    <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -->
    <div
      ref="emblaRef"
      class="pp-embla"
      :dir="emblaDir"
      role="region"
      :aria-label="t('book_player.label_page_thumbnails')"
      tabindex="0"
      @keydown="onKeyDown"
      @touchmove.passive="onContainerTouchMove"
      @touchend="onContainerTouchEnd"
      @touchcancel="onContainerTouchEnd"
    >
      <div class="pp-embla__container">
        <v-btn
          v-for="(item, i) in loi"
          :key="item.href + i"
          class="pp-slide pa-0"
          :class="{
            'pp-slide--active': isActive(item),
            'pp-slide--spread-left': item.isSpreadPairLeft,
            'pp-slide--spread-right': item.isSpreadPairRight,
          }"
          variant="text"
          :ripple="false"
          :aria-label="item.alt"
          :aria-current="isActive(item) ? 'page' : undefined"
          :aria-setsize="loi.length"
          :aria-posinset="i + 1"
          @click="goToContent(item)"
          @mouseenter="onSlideMouseEnter($event, item, i)"
          @mouseleave="clearHoverPreview"
          @focus="onSlideFocus($event, item, i)"
          @blur="clearHoverPreview"
        >
          <img v-if="loadedIndices.has(i)" :src="item.src" :alt="item.alt" class="pp-thumb" draggable="false" />
          <div v-else class="pp-thumb pp-thumb--skeleton" aria-hidden="true" />
          <span v-if="pageNumForItem(item) !== undefined" class="pp-slide__num" aria-hidden="true">{{
            pageNumForItem(item)
          }}</span>
        </v-btn>
      </div>
    </div>
  </div>

  <Teleport to="body">
    <Transition name="pp-preview">
      <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -->
      <div
        v-if="hoveredItem && previewStyle"
        class="pp-preview"
        :class="{ 'pp-preview--spread': hoveredPartner }"
        :style="previewStyle"
        aria-hidden="true"
        @mouseenter="keepHoverPreview"
        @mouseleave="clearHoverPreview"
        @focusin="keepHoverPreview"
        @focusout="clearHoverPreview"
      >
        <div class="pp-preview__pages">
          <img :src="previewLeftItem!.src" :alt="previewLeftItem!.alt" class="pp-preview__img" draggable="false" />
          <img
            v-if="previewRightItem"
            :src="previewRightItem.src"
            :alt="previewRightItem.alt"
            class="pp-preview__img"
            draggable="false"
          />
        </div>
        <div v-if="previewHasPageNum" class="pp-preview__label" aria-hidden="true">
          <template v-if="previewRightItem">
            {{ pageNumForItem(previewLeftItem!) }}–{{ pageNumForItem(previewRightItem) }}
          </template>
          <template v-else>
            {{ pageNumForItem(hoveredItem) }}
          </template>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style lang="scss" scoped>
.pp-embla-wrapper {
  display: flex;
  justify-content: center;
  height: 88px;
  overflow: hidden;
}

.pp-embla {
  position: relative;
  width: max-content;
  max-width: 100%;
  height: 100%;
  overflow: hidden;

  &:focus-visible {
    outline: 2px solid rgb(var(--v-theme-on-brand));
    outline-offset: -2px;
  }
}

.pp-embla__container {
  display: flex;
  height: 100%;
  align-items: center;
  gap: 1px;
  touch-action: pan-y pinch-zoom;
  will-change: transform;
}

.pp-slide {
  flex: 0 0 auto;
  position: relative;
  display: flex;
  align-items: center;
  padding: 2px;
  border: 2px solid transparent;
  border-radius: 4px;
  background: transparent;
  cursor: pointer;
  user-select: none;
  // Reset v-btn defaults
  min-width: 0 !important;
  height: auto !important;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease,
    transform 0.2s ease,
    opacity 0.2s ease;

  // Suppress Vuetify's hover/focus overlay — we handle states ourselves
  :deep(.v-btn__overlay) {
    display: none;
  }

  opacity: 0.35;
  transform: scale(0.78);

  .pp-slide__num {
    display: none;
  }

  &.pp-slide--active {
    opacity: 1;
    transform: scale(1);
    border-color: rgb(var(--v-theme-on-brand));
    border-radius: 6px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
  }

  &.pp-slide--spread-left {
    padding-right: 0;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    transform-origin: right center;
  }

  &.pp-slide--spread-right {
    padding-left: 0;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    transform-origin: left center;
  }

  &:focus-visible {
    outline: 2px solid rgb(var(--v-theme-on-brand));
    outline-offset: 2px;
  }
}

.pp-thumb {
  display: block;
  width: 52px;
  height: 70px;
  object-fit: cover;
  border-radius: 2px;
  pointer-events: none;
}

.pp-thumb--skeleton {
  background: linear-gradient(
    90deg,
    rgba(var(--v-theme-on-brand), 0.08) 25%,
    rgba(var(--v-theme-on-brand), 0.18) 50%,
    rgba(var(--v-theme-on-brand), 0.08) 75%
  );
  background-size: 200% 100%;
  animation: pp-shimmer 1.4s ease-in-out infinite;
}

@keyframes pp-shimmer {
  0% {
    background-position: 200% center;
  }
  100% {
    background-position: -200% center;
  }
}

// Desktop: compact strip (Option B) + page number badges (Option D)
@media (min-width: 960px) {
  .pp-embla-wrapper {
    height: 64px;
  }

  .pp-thumb {
    width: 38px;
    height: 52px;
  }

  .pp-slide__num {
    display: block;
    position: absolute;
    bottom: 4px;
    left: 50%;
    transform: translateX(-50%);
    font-size: 9px;
    line-height: 1;
    padding: 1px 4px;
    border-radius: 3px;
    background: rgba(0, 0, 0, 0.5);
    color: #fff;
    pointer-events: none;
    white-space: nowrap;
  }
}
</style>

<!-- Teleported popup lives outside the scoped component tree — global selectors required -->
<style lang="scss">
@use 'sass:map';
@use 'vuetify/settings' as v;

.pp-preview {
  position: fixed;
  z-index: 10000;
  width: 168px;
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.55);
  background: rgba(var(--v-theme-inverse-surface), 0.95);
  pointer-events: auto;

  &.pp-preview--spread {
    width: 280px;
  }

  @media (hover: none) {
    display: none;
  }

  @media (hover: none) and (#{map.get(v.$display-breakpoints, 'sm-and-up')}) {
    display: block;
  }
}

.pp-preview__pages {
  display: flex;
  gap: 2px;
}

.pp-preview__img {
  display: block;
  flex: 1;
  min-width: 0;
  height: auto;
  min-height: 72px;
  max-height: 240px;
  object-fit: cover;
}

.pp-preview__label {
  padding: 4px 6px;
  font-size: 11px;
  font-weight: 600;
  color: rgb(var(--v-theme-on-inverse-surface));
  text-align: center;
  background-color: rgba(var(--v-theme-inverse-surface), 0.95);
  letter-spacing: 0.02em;
}

.pp-preview-enter-active,
.pp-preview-leave-active {
  transition:
    opacity 0.12s ease,
    transform 0.12s ease;
}

.pp-preview-enter-from,
.pp-preview-leave-to {
  opacity: 0;
  transform: translateY(6px);
}
</style>
